'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import styles from './NavigationProgress.module.scss';

export default function NavigationProgress() {
  const pathname = usePathname();
  const [isNavigating, setIsNavigating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [prevPathname, setPrevPathname] = useState(pathname);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect route change during render (React-recommended pattern for responding to prop changes)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setProgress(100);
    setIsNavigating(false);
  }

  // Hide the bar after the completion animation
  useEffect(() => {
    if (progress === 100) {
      hideTimeoutRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);

      return () => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      };
    }
  }, [progress]);

  // Intercept link clicks to start the progress bar
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#') || href === pathname) return;

      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      setVisible(true);
      setIsNavigating(true);
      setProgress(15);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [pathname]);

  // Animate progress incrementally while navigating
  useEffect(() => {
    if (isNavigating) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          const increment = prev < 50 ? 8 : prev < 80 ? 3 : 1;
          return Math.min(prev + increment, 90);
        });
      }, 400);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isNavigating]);

  if (!visible) return null;

  return (
    <div className={styles.container}>
      <div
        className={`${styles.bar} ${progress === 100 ? styles.complete : ''}`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
